import { Controller } from "@hotwired/stimulus"
import { animate } from "motion"

/**
 * Floor Heating Calculator Controller
 * Calculates operating costs for radiant floor heating systems
 * 
 * Formula: cost = (kwh * sqft * (hours / 2.3) * wattage / 1000) / 100
 */
export default class extends Controller {
  static targets = [
    "sqftInput", "sqftDisplay", "hoursSelect", "hoursDisplay",
    "kwhInput", "kwhDisplay", "zipInput",
    "hourlyCost", "dailyCost", "monthlyCost",
    "systemSelect", "roomSelect",
    "results"
  ]

  static values = {
    sqft: { type: Number, default: 35 },
    hours: { type: Number, default: 4 },
    kwh: { type: Number, default: 12 },
    wattage: { type: Number, default: 15 }
  }

  connect() {
    this.calculate()
  }

  // Calculate and update display
  calculate() {
    // Get current values
    const sqft = this.hasSqftInputTarget ? parseFloat(this.sqftInputTarget.value) || this.sqftValue : this.sqftValue
    const hours = this.hasHoursSelectTarget ? parseInt(this.hoursSelectTarget.value) || this.hoursValue : this.hoursValue
    const kwh = this.kwhValue
    const wattage = this.wattageValue

    // Formula: (kwh * sqft * (hours / 2.3) * wattage / 1000) / 100
    const dailyCost = (kwh * sqft * (hours / 2.3) * wattage / 1000) / 100
    const hourlyCost = dailyCost / hours
    const monthlyCost = dailyCost * 30

    // Update display
    this.updateCostDisplay(hourlyCost, dailyCost, monthlyCost)
    this.updateHoursDisplay(hours)
    this.updateSqftDisplay(sqft)
    this.updateKwhDisplay(kwh)
  }

  updateCostDisplay(hourly, daily, monthly) {
    const costTargets = []
    
    if (this.hasHourlyCostTarget) {
      this.hourlyCostTarget.textContent = hourly.toFixed(2)
      costTargets.push(this.hourlyCostTarget.parentElement)
    }
    if (this.hasDailyCostTarget) {
      this.dailyCostTarget.textContent = daily.toFixed(2)
      costTargets.push(this.dailyCostTarget.parentElement)
    }
    if (this.hasMonthlyCostTarget) {
      this.monthlyCostTarget.textContent = monthly.toFixed(2)
      costTargets.push(this.monthlyCostTarget.parentElement)
    }
    
    // Animate the cost values with a pop effect
    this.animateCostChange(costTargets)
  }

  animateCostChange(targets) {
    targets.forEach((target) => {
      animate(
        target,
        { scale: [1, 1.15, 1], opacity: [0.7, 1] },
        { duration: 0.3, easing: "ease-out" }
      )
    })
  }

  updateHoursDisplay(hours) {
    if (this.hasHoursDisplayTarget) {
      this.hoursDisplayTarget.textContent = hours
    }
  }

  updateSqftDisplay(sqft) {
    if (this.hasSqftDisplayTarget) {
      this.sqftDisplayTarget.textContent = sqft
    }
  }

  updateKwhDisplay(kwh) {
    if (this.hasKwhDisplayTarget) {
      this.kwhDisplayTarget.textContent = kwh.toFixed(2)
    }
  }

  // Location methods - geolocation fills zip code, which then looks up rate
  useGeolocation() {
    if (!navigator.geolocation) {
      console.warn("Geolocation not supported")
      return
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords
        // Fetch location data which includes zip code and rate
        await this.fetchRateByCoords(latitude, longitude)
      },
      (error) => {
        console.error("Geolocation error:", error)
      },
      { enableHighAccuracy: false, timeout: 10000 }
    )
  }

  async lookupZipCode() {
    if (!this.hasZipInputTarget) return
    const zip = this.zipInputTarget.value.trim()
    if (!this.isValidZipCode(zip)) return

    await this.fetchRateByZip(zip)
  }

  updateKwh() {
    if (!this.hasKwhInputTarget) return
    const kwh = parseFloat(this.kwhInputTarget.value) || this.kwhValue
    this.kwhValue = kwh
    this.updateKwhDisplay(kwh)
    this.calculate()
  }

  // API calls
  async fetchRateByCoords(lat, lng) {
    try {
      const response = await fetch(`/api/v1/locations/by_lat_lng?lat=${lat}&lng=${lng}`)
      const data = await response.json()
      console.log("Geolocation API response:", data)
      
      if (!response.ok) {
        // API couldn't find rate for this location (e.g., outside US/Canada)
        console.warn("Could not find electricity rate for location:", data.message || "Unknown location")
        return
      }
      
      // Fill in zip code if available
      if (data.postal_code && this.hasZipInputTarget) {
        this.zipInputTarget.value = data.postal_code
      }
      const rate = this.extractRate(data)
      this.setKwhRate(rate)
    } catch (error) {
      console.error("Error fetching rate by coords:", error)
    }
  }

  async fetchRateByZip(zip) {
    try {
      const response = await fetch(`/api/v1/locations/by_postal_code/${zip}`)
      if (!response.ok) throw new Error("Failed to fetch rate")
      
      const data = await response.json()
      const rate = this.extractRate(data)
      this.setKwhRate(rate)
    } catch (error) {
      console.error("Error fetching rate by zip:", error)
    }
  }

  extractRate(data) {
    // API returns rate in dollars, convert to cents
    // Return null if no rate available (keeps current value)
    if (!data.average_rate) return null
    return parseFloat((data.average_rate * 100).toFixed(2))
  }

  setKwhRate(rate) {
    // Skip if no rate returned (keeps current value)
    if (rate === null || rate === undefined) return
    
    this.kwhValue = rate
    this.updateKwhDisplay(rate)
    if (this.hasKwhInputTarget) {
      this.kwhInputTarget.value = rate
    }
    this.calculate()
  }

  // Zip code validation (US and Canadian)
  isValidZipCode(zip) {
    const usOrCaZipcode = /(^\d{5}(-\d{4})?$)|(^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1} *\d{1}[A-Z]{1}\d{1}$)/i
    return usOrCaZipcode.test(zip)
  }

  // System selection (dropdown)
  selectSystem(event) {
    const wattage = parseInt(event.target.value)
    this.wattageValue = wattage
    this.calculate()
  }

  // Room selection (dropdown) - updates sqft input based on room type
  selectRoom(event) {
    const sqft = event.target.value
    if (sqft && sqft !== "null" && sqft !== "") {
      if (this.hasSqftInputTarget) {
        this.sqftInputTarget.value = sqft
      }
      this.updateSqftDisplay(sqft)
      this.calculate()
    }
  }

  // Hours selection (dropdown)
  selectHours(event) {
    const hours = parseInt(event.target.value)
    this.hoursValue = hours
    this.updateHoursDisplay(hours)
    this.calculate()
  }
}
